import React, { SyntheticEvent, useState } from 'react';
import {
	EntityType,
	ProjectState,
	resolveEntityName,
	searchStatementDefs,
	searchStatementFunctions,
	searchStatementState as searchStatementStateLib,
	searchStatementUtils,
} from '@elyx-code/project-logic-tree';
import {
	Autocomplete,
	Box,
	IconButton,
	TextField,
	Tooltip,
	Button
} from '@mui/material';
import {
	ENTITY_FA_ICON_NAMES,
	resolveEntitySubheader,
} from '../../../services/editor/ui';
import DefinitionEntityIcon from '../../draggable-entity-card/definition-entity/DefinitionEntityIcon';
import {
	SearchStatementBuilderTabType,
	useSearchStatementContext,
} from './search-statement-context';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { ValueSelector } from './ValueSelector';

const {
	NestedSearchStatement,
	SearchStatementState,
	DataSource,
	FunctionCall,
	SearchStatementLiteralValue
} = searchStatementStateLib;

const { SQLFunctionCategory, DataSourceType, SearchStatementNodeType } =
	searchStatementDefs;

const { SQL_FUNCTION_REGISTRY, getFunctionDefinition } =
	searchStatementFunctions;

type DataSource = searchStatementStateLib.DataSource;
type DataSourceType = searchStatementDefs.DataSourceType;
type NestedSearchStatement = searchStatementStateLib.NestedSearchStatement;
type SearchStatementState = searchStatementStateLib.SearchStatementState;
type SearchStatementLiteralValue =
	searchStatementStateLib.SearchStatementLiteralValue;
type FunctionCall = searchStatementStateLib.FunctionCall;
type SQLFunctionCategory = searchStatementDefs.SQLFunctionCategory;

export const DataSourceValue = ({
	value,
	id,
	hideLinkIcon = false,
}: {
	value: DataSource;
	id?: string;
	hideLinkIcon?: boolean;
}) => {
	const { project, tabManager } = useSearchStatementContext();

	const resolvedId =
		id || 'data-course-selector-value--' + ProjectState.UUID.uuid();

	if (value?.sourceType === DataSourceType.Table && !!value?.entity) {
		return (
			<span
				id={resolvedId}
				style={{
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				<span
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: 8,
					}}
				>
					<DefinitionEntityIcon entity={value.entity} size='small' />
					{resolveEntityName(value.entity, project.logic)}
				</span>
				<p
					style={{
						margin: 0,
						marginLeft: 20,
						fontSize: 10,
						color: 'rgba(0,0,0,0.54)',
					}}
				>
					{resolveEntitySubheader(value.entity)}
				</p>
			</span>
		);
	} else if (value?.sourceType === DataSourceType.Query) {
		return (
			<span
				id={resolvedId}
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}
			>
				<span
					style={{
						display: 'flex',
						flexDirection: 'column',
					}}
				>
					<span
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: 8,
							color: 'var(--color-background--search)',
						}}
					>
						<i
							style={{
								fontSize: 14,
							}}
							className={`canvas-draggable-card-header__icon fa-solid fa-${
								ENTITY_FA_ICON_NAMES[EntityType.Search]
							}`}
						/>
						{value.as || 'Nested search'}
					</span>
					<p
						style={{
							margin: 0,
							marginLeft: 20,
							fontSize: 10,
							color: 'rgba(0,0,0,0.54)',
						}}
					>
						A nested search statement that returns data
					</p>
				</span>

				{!hideLinkIcon && (
					<Tooltip
						title='Navigate to the nested search statement'
						arrow
						placement='top'
					>
						<IconButton
							aria-label='open-nested-search-statement'
							size='small'
							style={{ padding: 4, fontSize: 14, width: 22, height: 22 }}
							onClick={(e) => {
								e.stopPropagation();
								// Open on new tab
								tabManager.addTab(
									{
										entity: SearchStatementBuilderTabType.NestedQuery,
										query: (value.source as NestedSearchStatement).statement,
										parent: value.owner,
									},
									true,
								);
							}}
						>
							<OpenInNewIcon
								sx={{
									fontSize: 12,
								}}
							/>
						</IconButton>
					</Tooltip>
				)}
			</span>
		);
	} else if (value?.sourceType === DataSourceType.Aggregation) {
		return (
			<span
				id={resolvedId}
				style={{
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				<span
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: 8,
						color: 'var(--color-background--search)',
					}}
				>
					<i
						style={{
							fontSize: 14,
						}}
						className={`canvas-draggable-card-header__icon fa-solid fa-merge`}
					/>
					{value.as || 'Aggregation'}
				</span>
				<p
					style={{
						margin: 0,
						marginLeft: 20,
						fontSize: 10,
						color: 'rgba(0,0,0,0.54)',
					}}
				>
					Resulting data from the aggregation
				</p>
			</span>
		);
	} else if (value?.sourceType === DataSourceType.Function) {
		return (
			<span
				id={resolvedId}
				style={{
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				<span
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: 8,
						color: 'var(--color-main-primary)',
					}}
				>
					<i
						style={{ fontSize: 14 }}
						className={`canvas-draggable-card-header__icon fa-solid fa-function`}
					/>
					{value.functionCall?.functionName || 'Function'}(...)
				</span>
				<p
					style={{
						margin: 0,
						marginLeft: 20,
						fontSize: 10,
						color: 'rgba(0,0,0,0.54)',
					}}
				>
					A table-valued function
				</p>
			</span>
		);
	}

	return null;
};

const DataSourceSelection = ({
	onChange,
	onAliasChange,
	value,
	alias: extAlias = '',
	id,
	getOptionDisabled,
	getTooltip,
	label,
	options,
	alwaysShowAlias = false,
	canCreateNewNestedSearch = true,
}: {
	onChange: (value: DataSource | null) => void;
	onAliasChange: (alias: string) => void;
	value: DataSource;
	alias?: string;
	id: string;
	getOptionDisabled?: (option: DataSource) => boolean;
	getTooltip?: (option: DataSource) => React.ReactNode;
	label?: string;
	options: DataSource[];
	alwaysShowAlias?: boolean;
	canCreateNewNestedSearch?: boolean;
}) => {
	const { project, tabManager, searchStatementState, entity } =
		useSearchStatementContext();

	const handleDataSourceChange = (
		_: SyntheticEvent<Element, Event>,
		value: DataSource | null,
	) => {
		onChange(value);
	};

	const [alias, _setAlias] = useState(extAlias || value?.as || '');

	const setAlias = (alias: string) => {
		let sanitizedAlias = alias.trim().replace(/\s/g, '');
		_setAlias(sanitizedAlias);
	};

	const resolvedId = id || 'data-course-selector--' + ProjectState.UUID.uuid();

	// Extract Table-Valued Functions
	const tableFunctionOptions = Object.values(SQL_FUNCTION_REGISTRY)
		.filter(
			(def) =>
				def.category === SQLFunctionCategory.TableValued ||
				(!!value?.functionCall &&
					def.name === value.functionCall?.functionName),
		)
		.map((def) => ({ type: 'new-function', def }));

	const paddedOptions: any[] = [
		...(canCreateNewNestedSearch
			? [{ type: 'new-nested-search' } as const]
			: []),
		...tableFunctionOptions,
		...(options || []),
	];

	const resolvedOptions = paddedOptions.sort((a, b) => {
		if (a.type === 'new-nested-search') return -1;
		if (b.type === 'new-nested-search') return 1;
		if (a.type === 'new-function' && b.type !== 'new-function') return -1;
		if (a.type !== 'new-function' && b.type === 'new-function') return 1;

		if (!a.type && !b.type) {
			const aDisabled = getOptionDisabled?.(a as DataSource);
			const bDisabled = getOptionDisabled?.(b as DataSource);
			if (aDisabled && !bDisabled) return 1;
			else if (!aDisabled && bDisabled) return -1;
		}
		return 0;
	});

	const selectedFunctionDef: searchStatementFunctions.ISQLFunctionDefinition | null =
		(!!value?.functionCall?.functionName
			? getFunctionDefinition(value.functionCall.functionName)
			: null) || null;

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					gap: 12,
				}}
			>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: 6,
					}}
				>
					<Autocomplete
						id={id}
						disabled={!entity.editable}
						disablePortal
						options={resolvedOptions}
						sx={{ width: 200 }}
						value={value}
						onChange={handleDataSourceChange}
						getOptionDisabled={getOptionDisabled as any}
						getOptionLabel={(option) => {
							if (option.type === 'new-nested-search')
								return 'New nested search';
							else if (option.type === 'new-function')
								return `${option.def.name}()`;
							else if (option.sourceType === DataSourceType.Table)
								return resolveEntityName(option.entity, project.logic);
							else if (option.sourceType === DataSourceType.Query)
								return option.as || 'Nested search';
							else if (option.sourceType === DataSourceType.Function)
								return `${option.functionCall?.functionName}()`;
							return 'Unknown data source';
						}}
						renderOption={(props, option: any) => {
							const { key, ...optionProps } = props;

							if (option.type === 'new-nested-search') {
								return (
									<Box
										component='li'
										sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
										{...optionProps}
										onClick={(e) => {
											e.stopPropagation();
											const newNestedSearchStatement =
												new NestedSearchStatement(searchStatementState);
											const newSearchStatement = new SearchStatementState(
												ProjectState.UUID.uuid(),
											);
											const loadedDataSources =
												searchStatementUtils.loadProjectDatasources(
													project.logic,
												);

											loadedDataSources.forEach((ent) => {
												const newDataSource = new DataSource(
													newSearchStatement,
												);
												newDataSource.entity = ent;
												newDataSource.sourceType = DataSourceType.Table;
												newDataSource.as = ent.name;
												newSearchStatement.addDataSource(newDataSource);
											});

											newNestedSearchStatement.statement = newSearchStatement;
											const newDataSource = new DataSource(
												searchStatementState,
											);
											newDataSource.query = newNestedSearchStatement;
											newDataSource.sourceType = DataSourceType.Query;
											// Seed a default alias. Without one, the parent
											// aggregation's `isValid` is false (it requires
											// `!!this.as`), and the next toQuery() pass drops
											// the entire JOIN — silently destroying the user's
											// freshly-created subquery. The default is editable
											// via the Alias field; the suffix keeps it unique
											// when multiple nested searches sit in the same
											// statement. Order matters: sourceType must already
											// be Query so the DataSource.as setter propagates
											// through to NestedSearchStatement.as.
											newDataSource.as = newSearchStatement.mainDatabaseEntity?.name ? `${newSearchStatement.mainDatabaseEntity.name}_nested` : `nested_query_${ProjectState.UUID.uuid().slice(0, 8)}`;

											tabManager.addTab(
												{
													entity: SearchStatementBuilderTabType.NestedQuery,
													query: newSearchStatement,
													parent: searchStatementState,
												},
												true,
											);

											onChange(newDataSource);
										}}
									>
										<span style={{ display: 'flex', flexDirection: 'column' }}>
											<span
												style={{
													display: 'flex',
													alignItems: 'center',
													gap: 8,
													color: 'var(--color-background--search)',
												}}
											>
												<i
													style={{ fontSize: 14 }}
													className={`canvas-draggable-card-header__icon fa-solid fa-plus`}
												/>
												{'New nested search'}
											</span>
											<p
												style={{
													margin: 0,
													marginLeft: 20,
													fontSize: 10,
													color: 'rgba(0,0,0,0.54)',
												}}
											>
												Create a new nested search statement to draw the data
												from
											</p>
										</span>
									</Box>
								);
							}

							if (option.type === 'new-function') {
								const def =
									option.def as searchStatementFunctions.ISQLFunctionDefinition;
								return (
									<Box
										component='li'
										sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
										{...optionProps}
										onClick={(e) => {
											e.stopPropagation();
											const newDataSource = new DataSource(
												searchStatementState,
											);
											newDataSource.sourceType = DataSourceType.Function;

											const funcCall = new FunctionCall(searchStatementState);
											funcCall.functionName = def.name;
											newDataSource.functionCall = funcCall;
											newDataSource.as = `${def.name.toLowerCase()}_table`;

											onChange(newDataSource);
										}}
									>
										<span style={{ display: 'flex', flexDirection: 'column' }}>
											<span
												style={{
													display: 'flex',
													alignItems: 'center',
													gap: 8,
													color: 'var(--color-main-primary)',
												}}
											>
												<i
													style={{ fontSize: 14 }}
													className={`canvas-draggable-card-header__icon fa-solid fa-function`}
												/>
												{def.name}()
											</span>
											<p
												style={{
													margin: 0,
													marginLeft: 20,
													fontSize: 10,
													color: 'rgba(0,0,0,0.54)',
												}}
											>
												{def.description}
											</p>
										</span>
									</Box>
								);
							}

							return (
								<Tooltip
									key={key}
									title={getTooltip?.(option as DataSource)}
									placement='right'
									arrow
									enterDelay={
										getOptionDisabled?.(option as DataSource) ? 100 : 400
									}
									leaveDelay={200}
								>
									<Box
										component='li'
										sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
										{...optionProps}
									>
										<DataSourceValue value={option as DataSource} />
									</Box>
								</Tooltip>
							);
						}}
						renderInput={(params) => (
							<span
								style={{
									position: 'relative',
									display: 'block',
									height: '100%',
								}}
							>
								<span
									id={resolvedId + '--selected-value'}
									style={{
										position: 'absolute',
										top: 8,
										left: 0,
										width: '100%',
										height: '100%',
										maxHeight: 30,
										overflowY: 'hidden',
										paddingLeft: 14,
										display: 'flex',
										flexDirection: 'column',
									}}
								>
									<DataSourceValue value={value} hideLinkIcon />
								</span>

								<TextField
									{...params}
									size='small'
									label={label || 'Select data source'}
									slotProps={{
										htmlInput: {
											...params.inputProps,
											id: resolvedId + '--input',
											style: { opacity: 0 },
										},
									}}
									onFocus={(e) => {
										e.target.style.opacity = '1';
										const selectedValueElement = document.getElementById(
											resolvedId + '--selected-value',
										);
										if (selectedValueElement)
											selectedValueElement.style.visibility = 'hidden';
									}}
									onBlur={(e) => {
										e.target.style.opacity = '0';
										const selectedValueElement = document.getElementById(
											resolvedId + '--selected-value',
										);
										if (selectedValueElement)
											selectedValueElement.style.visibility = 'visible';
									}}
								/>
							</span>
						)}
					/>

					{value?.sourceType === DataSourceType.Query && (
						<Tooltip
							title='Navigate to the nested search statement'
							arrow
							placement='top'
						>
							<IconButton
								aria-label='open-nested-search-statement'
								size='small'
								disabled={!entity.editable}
								style={{ padding: 4, fontSize: 14, width: 22, height: 22 }}
								onClick={() => {
									tabManager.addTab(
										{
											entity: SearchStatementBuilderTabType.NestedQuery,
											query: (value.source as NestedSearchStatement).statement,
											parent: value.owner,
										},
										true,
									);
								}}
							>
								<OpenInNewIcon fontSize='small' />
							</IconButton>
						</Tooltip>
					)}
				</div>

				{/* Alias field */}
				{!!value &&
					(value?.sourceType !== DataSourceType.Table || alwaysShowAlias) && (
						<TextField
							label='Alias'
							variant='outlined'
							disabled={!entity.editable}
							style={{ width: 200 }}
							size='small'
							value={alias}
							onChange={(e) => setAlias(e.target.value)}
							onBlur={() => onAliasChange(alias)}
						/>
					)}
			</div>

			{/* --- RENDER FUNCTION ARGUMENTS IF FUNCTION IS SELECTED --- */}
			{value?.sourceType === DataSourceType.Function && value.functionCall && (
				<div style={{ paddingLeft: 12, maxWidth: 450 }}>
					{/* Render Arguments */}
					{selectedFunctionDef && (
						<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
							{(() => {
								// Same variadic-aware iteration as FunctionCallSelector: render
								// one slot per actual argument carried by the value, padded up
								// to the registry's required count.
								const slotCount = Math.max(
									value.functionCall.arguments.length,
									selectedFunctionDef.arguments.length,
								);
								const variadicArgDef = selectedFunctionDef.arguments.find(
									(a) => a.isVariadic,
								);
								const resolveArgDef = (i: number) =>
									selectedFunctionDef.arguments[i] ||
									variadicArgDef ||
									selectedFunctionDef.arguments[
										selectedFunctionDef.arguments.length - 1
									];
								return Array.from({ length: slotCount }).map((_, i) => {
									const argDef = resolveArgDef(i);
									return (
										<div
											key={i}
											style={{
												display: 'flex',
												// Same rationale as ValueSelector: pin the tick to
												// the top so multi-line nested function bodies don't
												// drag it to the vertical middle of the block.
												alignItems: 'flex-start',
												gap: 8,
											}}
										>
											<div
												style={{
													width: 12,
													height: 1,
													marginTop: 20,
													backgroundColor: 'rgba(0,0,0,0.2)',
													flexShrink: 0,
												}}
											/>
											<ValueSelector
												value={value.functionCall.arguments[i] as any}
												side={argDef.name as any}
												label={`Argument: ${argDef.name}`}
												options={searchStatementState.columns}
												acceptedTypes={argDef.acceptedTypes}
												disabledFunctionCategories={[]}
												onChange={(newArg) => {
													value.functionCall.arguments[i] = newArg;
													onChange(value);
												}}
												// disabled={false}
											/>
										</div>
									);
								});
							})()}

							{selectedFunctionDef.arguments.some((a) => a.isVariadic) && (
								<Button
									size='small'
									// disabled={false}
									onClick={() => {
										value.functionCall.arguments.push(
											new SearchStatementLiteralValue(searchStatementState),
										);
										onChange(value);
									}}
									style={{
										alignSelf: 'flex-start',
										fontSize: 10,
										marginLeft: 20,
									}}
								>
									+ Add Argument
								</Button>
							)}
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default DataSourceSelection;
